import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Association, DashboardStats } from '../types';
import api from '../services/api';
import Layout from '../components/Layout';

const Associations: React.FC = () => {
  const [associations, setAssociations] = useState<Association[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const [associationsRes, statsRes] = await Promise.all([
        api.getAssociations(),
        api.getDashboardStats(),
      ]);

      if (associationsRes.success) {
        setAssociations(associationsRes.data);
      }

      if (statsRes.success) {
        setStats(statsRes.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold text-heynay-dark-gray">Associations</h2>
            <p className="text-heynay-gray mt-1">Manage all associations in the Heynay platform</p>
          </div>
          <Link to="/associations/create">
            <button className="bg-heynay-blue text-white font-bold px-8 py-3 rounded-3xl hover:opacity-90 transition-opacity">
              + Create Association
            </button>
          </Link>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="bg-white border border-heynay-border rounded-2xl p-6">
              <h2 className="text-4xl font-bold text-heynay-dark-gray mb-2">{stats.totalAssociations}</h2>
              <p className="text-heynay-gray font-medium">Total Associations</p>
            </div>
            <div className="bg-white border border-heynay-border rounded-2xl p-6">
              <h2 className="text-4xl font-bold text-heynay-dark-gray mb-2">{stats.totalUsers}</h2>
              <p className="text-heynay-gray font-medium">Total Users</p>
            </div>
          </div>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-16">
          <div className="inline-block w-12 h-12 border-4 border-heynay-blue border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <>
          {/* Associations Grid */}
          {associations.length === 0 ? (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-6 py-12 rounded-xl text-center">
              <h5 className="font-bold mb-2">No associations found</h5>
              <p className="text-sm">Create your first association to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {associations.map((association) => (
                <div
                  key={association.associationId}
                  className="bg-white border border-heynay-border rounded-2xl overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                >
                  {/* Image */}
                  {association.image && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={association.image}
                        alt={association.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Card Body */}
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <h5 className="text-xl font-bold text-heynay-dark-gray">
                        {association.name}
                      </h5>
                      {association.isFrozen && (
                        <span className="inline-block bg-red-100 text-red-700 text-xs font-semibold px-2 py-0.5 rounded-lg">
                          Frozen
                        </span>
                      )}
                    </div>
                    <div className="mb-4">
                      <span className="inline-block bg-heynay-light-gray text-heynay-gray text-xs font-medium px-3 py-1.5 rounded-lg">
                        ID: {association.associationId}
                      </span>
                    </div>
                    <Link to={`/associations/${association.associationId}`}>
                      <button className="w-full bg-heynay-blue text-white font-bold py-2.5 rounded-3xl hover:opacity-90 transition-opacity text-sm">
                        View Details →
                      </button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Layout>
  );
};

export default Associations;
